<template>
  <Teleport to="body">
    <div v-if="visible" class="pwa-modal-overlay" @click.self="$emit('close')">
      <div class="pwa-modal">
        <button class="pwa-modal__close" type="button" @click="$emit('close')">
          <i class="zmdi zmdi-close" />
        </button>

        <div class="pwa-modal__icon-shell">
          <i class="zmdi zmdi-download" />
        </div>

        <h2 class="pwa-modal__title">{{ $t('pwa.title') }}</h2>

        <div class="pwa-modal__content">
          <div class="pwa-step">
            <div class="pwa-step__number">1</div>
            <div class="pwa-step__text">{{ step1 }}</div>
          </div>
          <div class="pwa-step">
            <div class="pwa-step__number">2</div>
            <div class="pwa-step__text">{{ step2 }}</div>
          </div>
        </div>

        <button class="pwa-modal__button" @click="$emit('close')">
          {{ $t('common.confirm') }}
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { isIOS, isAndroid, isChrome, isSafari } from '@/shared/utils/browser'

defineProps<{
  visible: boolean
}>()

defineEmits<{
  (e: 'close'): void
}>()

const { t } = useI18n()

const step1 = computed(() => {
  if (isIOS()) return t('pwa.ios.step1')
  if (isAndroid()) return t('pwa.android.step1')
  if (isChrome() || !isSafari()) return t('pwa.desktop.step1')
  return t('pwa.generic.step1')
})

const step2 = computed(() => {
  if (isIOS()) return t('pwa.ios.step2')
  if (isAndroid()) return t('pwa.android.step2')
  if (isChrome() || !isSafari()) return t('pwa.desktop.step2')
  return t('pwa.generic.step2')
})
</script>

<style scoped lang="scss">
.pwa-modal-overlay {
  position: fixed;
  inset: 0;
  background: var(--ds-scrim);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 16px;
}

.pwa-modal {
  background: var(--hm-surface);
  border-radius: var(--p-radius-28);
  width: 100%;
  max-width: 400px;
  padding: 40px 32px 32px;
  position: relative;
  box-shadow: 0 24px 48px var(--ds-scrim-faint);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: modalSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.pwa-modal__close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  border-radius: var(--p-radius-50);
  background: var(--hm-surface-alt);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--hm-text-secondary);
  transition: all 0.2s;

  i {
    font-size: var(--ds-text-size-h-sm);
  }

  &:hover {
    background: var(--ds-accent-soft-08);
    color: var(--hm-accent);
  }
}

.pwa-modal__icon-shell {
  width: 64px;
  height: 64px;
  border-radius: var(--p-radius-20);
  background: linear-gradient(135deg, var(--hm-accent) 0%, var(--hm-accent-light) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  box-shadow: 0 8px 16px var(--ds-accent-soft-20);

  i {
    font-size: var(--p-text-32);
    color: var(--hm-text-primary);
  }
}

.pwa-modal__title {
  font-size: var(--ds-text-size-h-md);
  font-weight: var(--ds-weight-bold);
  color: var(--hm-text-primary);
  margin: 0 0 32px;
}

.pwa-modal__content {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 32px;
}

.pwa-step {
  display: flex;
  align-items: center;
  gap: 16px;
  text-align: left;
  background: var(--hm-surface-alt);
  padding: 16px;
  border-radius: var(--ds-radius-card-lg);
  border: 1px solid var(--hm-border);
}

.pwa-step__number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--hm-accent);
  color: var(--hm-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--ds-text-size-body);
  font-weight: var(--ds-weight-bold);
  flex-shrink: 0;
}

.pwa-step__text {
  font-size: var(--p-text-15);
  line-height: 1.4;
  color: var(--hm-text-secondary);
  font-weight: var(--ds-weight-medium);
}

.pwa-modal__button {
  width: 100%;
  padding: 16px;
  border-radius: var(--p-radius-50);
  background: var(--hm-accent);
  color: var(--hm-text-primary);
  border: none;
  font-size: var(--ds-text-size-body-lg);
  font-weight: var(--ds-weight-semibold);
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px var(--ds-accent-soft-20);

  &:hover {
    background: var(--hm-accent-light);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px var(--ds-accent-soft-25);
  }

  &:active {
    transform: translateY(0);
  }
}

@media (max-width: 480px) {
  .pwa-modal-overlay {
    padding: 0;
    align-items: flex-end;
  }

  .pwa-modal {
    max-width: 100%;
    border-radius: var(--ds-radius-modal) var(--ds-radius-modal) 0 0;
    padding: 32px 20px 40px;
    animation: modalSlideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  @keyframes modalSlideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
}
</style>
